import axios from 'axios';

// Определяем базовый URL API
const getApiBaseUrl = () => {
  // В режиме разработки используем прокси через Vite (/api)
  if (import.meta.env.DEV) {
    return '/api';
  }
  
  // В production используем полный URL из переменной окружения
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Fallback для production
  const protocol = 'http';
  const host = import.meta.env.VITE_API_HOST || 'localhost';
  const port = import.meta.env.VITE_API_PORT || '3000';
  
  return `${protocol}://${host}:${port}`;
};

const API_BASE_URL = getApiBaseUrl();

// Логируем базовый URL при загрузке
console.log('🔗 API Base URL:', API_BASE_URL);
console.log('📱 User Agent:', navigator.userAgent);
console.log('🌐 Current URL:', window.location.href);
console.log('🔄 Proxy mode:', import.meta.env.DEV ? 'enabled (via Vite)' : 'disabled');

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 секунд таймаут
});

// Добавляем обработчик запросов для отладки
api.interceptors.request.use(
  (config) => {
    const fullUrl = config.baseURL?.startsWith('/') 
      ? `${window.location.origin}${config.baseURL}${config.url}`
      : `${config.baseURL}${config.url}`;
    const timestamp = new Date().toISOString();
    const isProxy = config.baseURL === '/api';
    
    console.log(`[${timestamp}] 🌐 API Request:`, {
      method: config.method?.toUpperCase(),
      url: fullUrl,
      baseURL: config.baseURL,
      path: config.url,
      proxy: isProxy ? '✅ (via Vite)' : '❌ (direct)',
    });
    
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', {
      message: error.message,
      code: error.code,
      config: error.config,
    });
    return Promise.reject(error);
  }
);

// Добавляем обработчик ответов для обработки ошибок
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Response [${response.status}]:`, {
      url: response.config.url,
      data: response.data,
    });
    return response;
  },
  (error) => {
    const errorInfo = {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      fullURL: error.config ? `${error.config.baseURL}${error.config.url}` : null,
      timestamp: new Date().toISOString(),
    };

    console.error('❌ API Response Error:', errorInfo);

    // Дополнительная информация для сетевых ошибок
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.error('🔴 Network Error Details:', {
        'Проверьте': [
          '1. Сервер запущен и доступен',
          '2. Правильный IP адрес (не localhost для мобильных)',
          `3. URL должен быть: ${error.config?.baseURL}`,
          '4. Сертификат принят в браузере',
          '5. CORS настроен правильно',
        ],
        'Текущий URL': window.location.href,
        'API Base URL': error.config?.baseURL,
        'User Agent': navigator.userAgent,
      });
    }

    return Promise.reject(error);
  }
);

// Products API
export const productsApi = {
  getAll: () => api.get('/products'),
  getById: (id) => api.get(`/products/${id}`),
  getByQr: (qr) => api.get(`/products/by-qr/${encodeURIComponent(qr)}`),
  getByPc: (pc) => api.get(`/products/by-pc/${encodeURIComponent(pc)}`),
  getByItemCode: (itemCode) => api.get(`/products/by-item-code/${encodeURIComponent(itemCode)}`),
  search: (query) => api.get(`/products/search?q=${encodeURIComponent(query)}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.patch(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Stock API
export const stockApi = {
  stockIn: (productId, qty, dioptry) => api.post('/stock/in', { product_id: productId, qty, dioptry }),
  stockOut: (productId, qty, dioptry) => api.post('/stock/out', { product_id: productId, qty, dioptry }),
  getStock: (productId) => api.get(`/stock/${productId}`),
};

// Orders API
export const ordersApi = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  getByPhone: (phone) => api.get(`/orders/by-phone/${encodeURIComponent(phone)}`),
  markAsIssued: (id) => api.patch(`/orders/${id}/issue`),
  updatePayment: (id, paidAmount) => api.patch(`/orders/${id}/payment`, { paid_amount: paidAmount }),
  delete: (id) => api.delete(`/orders/${id}`),
};

// Statistics API
export const statisticsApi = {
  getStatistics: (period = '1m', startDate = null, endDate = null) => {
    let url = `/statistics?period=${period}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return api.get(url);
  },
};

// China Orders API
export const chinaOrdersApi = {
  getAll: () => api.get('/china-orders'),
  getById: (id) => api.get(`/china-orders/${id}`),
  create: (data) => api.post('/china-orders', data),
  receive: (id) => api.post(`/china-orders/${id}/receive`),
  delete: (id) => api.delete(`/china-orders/${id}`),
};

